import { useUrlSearchParams } from '@dai/common'
import { Box } from '@mui/material'
import dayjs, { Dayjs } from 'dayjs'
import React from 'react'

import { MUIDatePicker } from '../forms/components/MUIDatePicker'
import { AutocompleteWithUrlParams } from './AutocompleteWithUrlParams'
import { PreCannedDatePicker } from './PreCannedDatePicker'
import { RemoteSearchableSelectWithUrlParams } from './RemoteSearchableSelectWithUrlParams'

export type FilterOptions = {
  date: {
    urlKey: string
  }
  preCannedDatePicker: { startDateKey: string; endDateKey: string }
  search: {
    urlKey: string
    multiselect: boolean
    query: any // graphql query
    getOptions: (data: any) => { label: string; value: string }[]
    getVariablesForQuery: (textInput: string) => any
    getOneQuery: any // graphql query
    getOneVariables: (id: string) => any
    getOneMap: (data: any) => { [urlKey: string]: string } | null
  }
  select: {
    urlKey: string
    options: { value: string; label: string }[]
    multiselect: boolean
    loading: boolean
  }
}

export type Filter = {
  type: keyof FilterOptions
  label: string
  options: FilterOptions[Filter['type']]
}

type FilterBarProps = {
  filters: Filter[]
}

const FilterComponent = ({ filter }: { filter: Filter }) => {
  const {
    state: urlState,
    handle: { handleUpdateSearchParams },
  } = useUrlSearchParams()

  const handleDateChange = (date: Dayjs | null, urlKey: string) => {
    const newParams: Record<string, string | undefined> = {
      [urlKey]: date?.format('YYYY-MM-DD'),
    }

    const currentStartDate = urlState.startDate as string
    const currentEndDate = urlState.endDate as string

    if (urlKey === 'startDate' && date && currentEndDate) {
      const endDate = dayjs(currentEndDate)
      if (date.isAfter(endDate)) {
        newParams.endDate = date.format('YYYY-MM-DD')
      }
    } else if (urlKey === 'endDate' && date && currentStartDate) {
      const startDate = dayjs(currentStartDate)
      if (date.isBefore(startDate)) {
        newParams.startDate = date.format('YYYY-MM-DD')
      }
    }

    handleUpdateSearchParams(newParams)
  }

  switch (filter.type) {
    case 'date': {
      const dateOptions = filter.options as FilterOptions['date']
      const date = urlState[dateOptions.urlKey] as string
      return (
        <MUIDatePicker
          onDateChange={date => handleDateChange(date, dateOptions.urlKey)}
          date={date ? dayjs(date) : null}
          maxDate={dayjs()}
          label={filter.label}
          sx={{ flex: 1, height: '100%', minWidth: 150 }}
        />
      )
    }
    case 'preCannedDatePicker': {
      const options = filter.options as FilterOptions['preCannedDatePicker']
      return (
        <PreCannedDatePicker
          startDateKey={options.startDateKey}
          endDateKey={options.endDateKey}
        />
      )
    }
    case 'search': {
      const searchOptions = filter.options as FilterOptions['search']
      return (
        <RemoteSearchableSelectWithUrlParams
          {...searchOptions}
          label={filter.label}
        />
      )
    }
    case 'select': {
      const selectOptions = filter.options as FilterOptions['select']
      return (
        <AutocompleteWithUrlParams label={filter.label} {...selectOptions} />
      )
    }
    default:
      throw new Error(`Filter type ${filter.type} not supported`)
  }
}

export const FilterBar: React.FC<FilterBarProps> = props => {
  const { filters } = props
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        gap: 2,
        alignItems: 'center',
        height: 70,
        overflowX: 'auto',
      }}
    >
      {filters.map(filter => (
        <Box key={filter.label} sx={{ display: 'flex', gap: 2 }}>
          <FilterComponent filter={filter} />
        </Box>
      ))}
    </Box>
  )
}
